import { Component, OnInit, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { LicensePlateRecognitionService, LicensePlateRecognitionDto, CameraService, CameraDto, PointDirection } from '../../../../../../api/gen';
import { JJamesCardComponent, JjamesButtonComponent } from 'shared-lib';

@Component({
  selector: 'app-camera-detection-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    JJamesCardComponent,
    JjamesButtonComponent
  ],
  templateUrl: './camera-detection-list.component.html',
  styleUrl: './camera-detection-list.component.scss'
})
export class CameraDetectionListComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private lprService = inject(LicensePlateRecognitionService);
  private cameraService = inject(CameraService);
  private snackBar = inject(MatSnackBar);

  cameraId = signal<string | null>(null);
  camera = signal<CameraDto | null>(null);
  isLoading = signal(false);

  // Filter signals
  plateNumber = signal<string | undefined>(undefined);
  direction = signal<PointDirection | undefined>(undefined);
  startDate = signal<string | undefined>(undefined);
  endDate = signal<string | undefined>(undefined);

  dataSource = new MatTableDataSource<LicensePlateRecognitionDto>([]);
  displayedColumns: string[] = ['detectionTime', 'plateNumber', 'confidence', 'direction', 'actions'];

  pageSize = signal(10);
  pageIndex = signal(0);
  totalCount = signal(0);

  showImages: { [key: string]: boolean } = {};

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = params['cameraId'];
      if (id) {
        this.cameraId.set(id);
        this.loadCameraDetails(id);
        this.loadDetections();
      }
    });
  }

  loadCameraDetails(id: string): void {
    this.cameraService.camerasCameraIdGet(id).subscribe({
      next: (camera) => this.camera.set(camera),
      error: (err) => console.error('Error loading camera details', err)
    });
  }

  loadDetections(): void {
    const id = this.cameraId();
    if (!id) return;

    this.isLoading.set(true);
    this.lprService.licensePlateRecognitionGet(
      this.plateNumber(), // plateNumber
      id,                 // cameraId
      this.startDate() ? new Date(this.startDate()!).toISOString() : undefined, // startDate (UTC)
      this.endDate() ? new Date(this.endDate()!).toISOString() : undefined, // endDate (UTC)
      this.direction(),   // direction
      undefined,          // minConfidence
      this.pageIndex(),
      this.pageSize()
    ).subscribe({
      next: (result) => {
        this.dataSource.data = result.items || [];
        this.totalCount.set(result.totalCount || 0);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading detections', err);
        this.snackBar.open('Error loading detections', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadDetections();
  }

  toggleImage(id: string): void {
    this.showImages[id] = !this.showImages[id];
  }

  clearFilters(): void {
    this.plateNumber.set(undefined);
    this.direction.set(undefined);
    this.startDate.set(undefined);
    this.endDate.set(undefined);
    this.pageIndex.set(0);
    this.loadDetections();
  }

  goBack(): void {
    window.history.back();
  }

  onImageError(event: any): void {
    console.error('Error loading image from Azure Blob', event);
    // Optionally show a fallback or handle the error
  }
}
